import type { DecimalString, Uuid } from './subprojects';

export type OrderStatus = 'borrador' | 'pedido' | 'confirmado' | 'enviado' | 'recibido' | 'cancelado';

export interface OrderItem {
  id: Uuid;
  order_id: Uuid;
  room_item_id: Uuid | null;
  description: string;
  quantity: number;
  unit_cost: DecimalString;
  line_total_cost: DecimalString;
  created_at: string;
}

export interface Order {
  id: Uuid;
  project_id: Uuid;
  subproject_id: Uuid;
  supplier_id: Uuid;
  shipping_location_id: Uuid | null;
  order_number: string;
  tracking_number: string | null;
  status: OrderStatus;
  supplier_called: boolean;
  supplier_called_at: string | null;
  supplier_confirmed: boolean;
  supplier_emailed: boolean;
  supplier_emailed_at: string | null;
  supplier_emailed_to: string | null;
  supplier_emailed_subject: string | null;
  supplier_emailed_body: string | null;
  supplier_emailed_attachments: string[] | null;
  shipping_location: string | null;
  expected_delivery_date: string | null;
  total_cost: DecimalString;
  created_at: string;
  updated_at: string;
  notes: string | null;
  items: OrderItem[];
  project_name?: string | null;
  subproject_name?: string | null;
  supplier_name?: string | null;
  supplier_email?: string | null;
}

export interface OrderItemPayload {
  room_item_id?: Uuid | null;
  description: string;
  quantity?: number;
  unit_cost?: DecimalString;
  line_total_cost?: DecimalString | null;
}

export interface OrderCreatePayload {
  project_id: Uuid;
  subproject_id: Uuid;
  supplier_id: Uuid;
  order_number?: string;
  tracking_number?: string | null;
  status?: OrderStatus;
  supplier_called?: boolean;
  supplier_confirmed?: boolean;
  shipping_location_id?: Uuid | null;
  expected_delivery_date?: string | null;
  total_cost?: DecimalString;
  notes?: string | null;
  items?: OrderItemPayload[];
}

export type OrderUpdatePayload = Partial<OrderCreatePayload> & { items?: OrderItemPayload[] };

export interface OrderEmailPayload {
  to_email: string;
  subject: string;
  body: string;
  extra_attachments?: File[];
}

export interface OrderFilters {
  search?: string;
  status?: OrderStatus;
  supplier?: Uuid;
  project?: Uuid;
  subproject?: Uuid;
  supplier_called?: boolean;
}

export interface OrderPagination { page: number; page_size: number; total?: number; total_pages?: number; }

export interface OrdersResponse { items: Order[]; pagination: OrderPagination; }

export function normalizeOrdersResponse(raw: any): OrdersResponse {
  const rawItems = Array.isArray(raw) ? raw : raw?.items ?? raw?.data ?? [];
  const items = Array.isArray(rawItems) ? rawItems : Array.isArray(rawItems.items) ? rawItems.items : [];
  const pagination = raw?.pagination ?? raw?.data?.pagination ?? {};
  const fallbackPageSize = items.length || 1;

  return {
    items,
    pagination: {
      total: pagination.total ?? raw?.total ?? items.length,
      page: pagination.page ?? raw?.page ?? 1,
      page_size: pagination.page_size ?? raw?.page_size ?? raw?.per_page ?? fallbackPageSize,
      total_pages:
        pagination.total_pages ??
        raw?.total_pages ??
        Math.max(
          1,
          Math.ceil(
            (pagination.total ?? raw?.total ?? items.length)
            / (pagination.page_size ?? raw?.page_size ?? raw?.per_page ?? fallbackPageSize),
          ),
        ),
    },
  };
}
